import { Box, Typography, DialogContentText } from '@mui/material';
import { useLocation } from 'react-router-dom';
import React, { useState, useImperativeHandle, forwardRef, useRef } from 'react';
import dayjs from 'dayjs';

import { UserAnswers, hasReimbursableExpenses, hasNoReimbursements, getAnswersForValidation } from '../types';

import ReimbursementQuestionnaire, { ReimbursementQuestionnaireRef } from './components/ReimbursementQuestionnaire';
import ReimbursementResults from './components/ReimbursementResults';

import { useMember } from 'modules/common/queries/member';
import createAccountablePlanRequest from 'services/bookkeeping/mutations/useCreateAccountablePlan/useCreateAccountablePlan.request';
import useCompleteOnboardingStep from 'services/bookkeeping/mutations/useCompleteOnboardingStep';
import { ParsedAccountablePlanData } from 'services/bookkeeping/queries/useAccountablePlans/useAccountablePlans.interface';
import { undoCastPlanValues, generateEmptyAccountablePlan } from 'modules/common/components/AccountablePlanV2/utils';
import {
    calculateMileageReimbursableAmount,
    calculatePhoneAndInternetReimbursableAmount,
    calculateHomeOfficeReimbursableAmount,
} from 'modules/common/components/AccountablePlanV2/calculations';
import { selectLatestMileageRate } from 'modules/common/components/AccountablePlanV2/MixedUse/Mileage/utils';
import useMileageRates from 'services/bookkeeping/queries/useMileageRates';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import ConfirmationDialog from 'modules/common/components/MUI/ConfirmationDialog';

interface LocationState {
    userAnswers?: UserAnswers;
}

// How long the "No reimbursements needed" message is held on screen before the step is completed
// and the member is moved on. Exported so the test drives the timer off the same value.
export const NO_REIMBURSEMENTS_MESSAGE_DURATION_MS = 2000;

interface ReimbursementOverviewProps {
    setCanProceed?: (canProceed: boolean) => void;
    onNext?: () => void;
    /** Called when the step switches between the questionnaire and the results, so its footer can follow. */
    onViewChange?: (showQuestionnaire: boolean) => void;
}

const defaultProps: Partial<ReimbursementOverviewProps> = {
    setCanProceed: () => {},
    onNext: () => {},
    onViewChange: () => {},
};

export interface ReimbursementOverviewRef {
    // Settles when this click's work is done, on every path. On the questionnaire that covers the
    // no-reimbursement branch's delayed write; on the results phase it stays pending across the
    // confirmation modal and the accountable-plan writes it kicks off, and settles when the member
    // cancels out of the modal. Callers can therefore hold their pending state on it for the whole
    // step, which is what stops a second click creating a duplicate set of plans.
    handleComplete: () => Promise<void>;
    handleBack: () => void;
    showQuestionnaire: boolean;
}

// Helper function to transform UserAnswers and monthly expense data to multiple ParsedAccountablePlanData
// Uses AccountablePlanV2 utilities for proper calculations
const transformUserAnswersToAccountablePlans = (
    userAnswers: UserAnswers,
    monthlyExpenseData: {
        [month: string]: {
            homeOffice: number;
            phoneInternet: number;
            mileage: number;
            cost_security?: number;
            cost_cleaning?: number;
            cost_utilities?: number;
            cost_rent?: number;
            cost_rent_insurance?: number;
            cost_HOA?: number;
            cost_home_insurance?: number;
            cost_property_taxes?: number;
            cost_mortgage_interest?: number;
            cost_internet?: number;
            cost_phone?: number;
            businessMiles?: number;
            phoneInternetBusinessPercent?: number;
        };
    },
    memberId: number,
    mileageRates: { id: number; rate: string; effective_date: string }[] = [],
    year: number = new Date().getFullYear()
): ParsedAccountablePlanData[] => {
    const isRenting = userAnswers.rentOrOwn === 'rent';
    const hasHomeOffice = userAnswers.rentsOfficeSpace === 'no' && userAnswers.usesHomeAsOffice === 'yes';
    const drivesForBusiness = userAnswers.drivesForBusiness === 'yes';

    const plans: ParsedAccountablePlanData[] = [];

    Object.entries(monthlyExpenseData).forEach(([monthName, expenseData]) => {
        // Skip months with no expenses
        if (expenseData.homeOffice === 0 && expenseData.phoneInternet === 0 && expenseData.mileage === 0) {
            return;
        }

        // Convert month name to month number and create dayjs object
        const monthNumber = new Date(`${monthName} 1, ${year}`).getMonth() + 1;
        const planPeriod = dayjs()
            .year(year)
            .month(monthNumber - 1);

        // Generate base plan using AccountablePlanV2 utilities
        const basePlan = generateEmptyAccountablePlan(planPeriod, memberId);

        // Update with user-specific data
        const plan: ParsedAccountablePlanData = {
            ...basePlan,
            is_renting: isRenting,
            square_footage_home: hasHomeOffice ? parseFloat(userAnswers.totalHomeSqft) || 0 : 0,
            square_footage_office: hasHomeOffice ? parseFloat(userAnswers.officeSqft) || 0 : 0,
            // Phone/internet business percentage (from user input in modal, defaults to 70%)
            internet_phone_business_percent: Math.round(expenseData.phoneInternetBusinessPercent || 70),
            // Mileage data
            miles_driven_total: drivesForBusiness ? Math.round(expenseData.businessMiles || expenseData.mileage) : 0,
            miles_driven_business: drivesForBusiness ? Math.round(expenseData.businessMiles || expenseData.mileage) : 0,
            // Individual expense costs from the detailed form
            cost_security: Math.round((expenseData.cost_security || 0) * 100) / 100,
            cost_cleaning: Math.round((expenseData.cost_cleaning || 0) * 100) / 100,
            cost_utilities: Math.round((expenseData.cost_utilities || 0) * 100) / 100,
            cost_HOA: Math.round((expenseData.cost_HOA || 0) * 100) / 100,
            cost_home_insurance: Math.round((expenseData.cost_home_insurance || 0) * 100) / 100,
            cost_property_taxes: Math.round((expenseData.cost_property_taxes || 0) * 100) / 100,
            cost_mortgage_interest: Math.round((expenseData.cost_mortgage_interest || 0) * 100) / 100,
            cost_rent: Math.round((expenseData.cost_rent || 0) * 100) / 100,
            cost_rent_insurance: Math.round((expenseData.cost_rent_insurance || 0) * 100) / 100,
            // Phone and internet costs
            cost_internet: Math.round((expenseData.cost_internet || 0) * 100) / 100,
            cost_phone: Math.round((expenseData.cost_phone || 0) * 100) / 100,
        };

        // Use AccountablePlanV2 calculation functions
        // Get the correct IRS mileage rate for the year
        const mileageRateData = selectLatestMileageRate(mileageRates, planPeriod);
        const mileageRate = parseFloat(mileageRateData?.rate || '0.70'); // Fallback to 0.70 if no rate found
        const mileageReimbursable = calculateMileageReimbursableAmount(plan, mileageRate);
        const phoneInternetReimbursable = calculatePhoneAndInternetReimbursableAmount(plan);
        const homeOfficeReimbursable = calculateHomeOfficeReimbursableAmount(plan);

        // Calculate total reimbursements using proper calculations
        plan.total_reimbursements = parseFloat(
            (homeOfficeReimbursable + phoneInternetReimbursable + mileageReimbursable).toFixed(2)
        );

        plans.push(plan);
    });

    return plans;
};

const ReimbursementOverview = forwardRef<ReimbursementOverviewRef, ReimbursementOverviewProps>(
    ({ setCanProceed, onNext, onViewChange }, ref) => {
        // Use ref to store setCanProceed to avoid dependency issues
        const setCanProceedRef = React.useRef(setCanProceed);
        setCanProceedRef.current = setCanProceed;

        // Stable function that uses the ref
        const stableSetCanProceed = React.useCallback(
            (canProceed: boolean) => {
                setCanProceedRef.current?.(canProceed);
            },
            [] // Empty dependency array is safe now
        );

        const location = useLocation();
        const { data: member } = useMember();
        const { data: mileageRates } = useMileageRates();
        const questionnaireRef = useRef<ReimbursementQuestionnaireRef>(null);

        // Resolver for the promise handleComplete hands back on the results phase. Held in a ref
        // rather than state because nothing renders off it, and resolving it is what releases the
        // footer button's pending state — so it must be settled on every way out of the modal,
        // confirm and cancel alike, or Complete stays disabled forever.
        const confirmCompletionResolveRef = useRef<(() => void) | null>(null);
        const completeOnboardingStep = useCompleteOnboardingStep();
        const { sendTrackingEvent } = useEventTracking();
        // Get user answers from location state or initialize with defaults
        const locationState = location.state as LocationState;
        const initialValues: UserAnswers = locationState?.userAnswers || {
            drivesForBusiness: '',
            rentsOfficeSpace: '',
            usesHomeAsOffice: '',
            rentOrOwn: '',
            officeSqft: '',
            totalHomeSqft: '',
            percentage: '0',
        };

        const [questionnaireComplete, setQuestionnaireComplete] = useState(false);
        const [showQuestionnaire, setShowQuestionnaire] = useState(true);
        React.useEffect(() => {
            onViewChange?.(showQuestionnaire);
        }, [showQuestionnaire, onViewChange]);
        const [formValues, setFormValues] = useState<UserAnswers>(initialValues);
        const [showConfirmModal, setShowConfirmModal] = useState(false);
        const [isConfirming, setIsConfirming] = useState(false);
        // The state lags a render behind, so a second click before it lands still gets through without this.
        const isConfirmingRef = useRef(false);
        const [showNoReimbursementsMessage, setShowNoReimbursementsMessage] = useState(false);
        const [monthlyExpenseData, setMonthlyExpenseData] = useState<{
            [month: string]: {
                homeOffice: number;
                phoneInternet: number;
                mileage: number;
                // Individual expense details for proper accountable plan mapping
                cost_security?: number;
                cost_cleaning?: number;
                cost_utilities?: number;
                cost_rent?: number;
                cost_rent_insurance?: number;
                cost_HOA?: number;
                cost_home_insurance?: number;
                cost_property_taxes?: number;
                cost_mortgage_interest?: number;
                cost_internet?: number;
                cost_phone?: number;
                businessMiles?: number;
                phoneInternetBusinessPercent?: number;
            };
        }>({});

        const settleConfirmCompletion = () => {
            confirmCompletionResolveRef.current?.();
            confirmCompletionResolveRef.current = null;
        };

        const cancelConfirmModal = () => {
            setShowConfirmModal(false);
            settleConfirmCompletion();
        };

        // Initialize with form disabled on component mount (only once)
        React.useEffect(() => {
            stableSetCanProceed(false);
        }, [stableSetCanProceed]); // Now safe to include stableSetCanProceed

        // Track reimbursement section started -- once per mount
        const hasTrackedSectionStarted = useRef(false);
        React.useEffect(() => {
            if (hasTrackedSectionStarted.current) {
                return;
            }

            hasTrackedSectionStarted.current = true;
            sendTrackingEvent('Reimbursement Section Started', {
                category: 'accounting_setup',
                action: 'section_started',
                stepName: 'reimbursement_overview',
                userType: member?.usertype,
            });
        }, [sendTrackingEvent, member?.usertype]);

        // Format member's home address for display
        const homeAddress = member?.one_line_home_address || 'your home';

        const handleSubmit = async (values: UserAnswers) => {
            setFormValues(values);
            setQuestionnaireComplete(true);

            // Use cleaned data for business logic decisions
            const cleanedValues = getAnswersForValidation(values);

            // Track questionnaire completion
            sendTrackingEvent('Reimbursement Questionnaire Completed', {
                category: 'accounting_setup',
                action: 'questionnaire_completed',
                stepName: 'reimbursement_overview',
                // Include all answers as properties
                drivesForBusiness: values.drivesForBusiness,
                rentsOfficeSpace: values.rentsOfficeSpace,
                usesHomeAsOffice: values.usesHomeAsOffice,
                rentOrOwn: values.rentOrOwn,
                officeSqft: values.officeSqft,
                totalHomeSqft: values.totalHomeSqft,
                percentage: values.percentage,
                hasReimbursableExpenses: hasReimbursableExpenses(cleanedValues),
                noReimbursementsNeeded: hasNoReimbursements(cleanedValues),
            });

            // Fired here on submit, not from a values effect in the questionnaire: that effect re-ran on
            // every keystroke in the square-footage fields, about 27 events per member.
            if (values.usesHomeAsOffice === 'yes' && values.rentOrOwn && values.officeSqft && values.totalHomeSqft) {
                sendTrackingEvent('Home Office Details Provided', {
                    category: 'accounting_setup',
                    action: 'home_office_details_provided',
                    stepName: 'reimbursement_questionnaire',
                    officeSqft: values.officeSqft,
                    totalHomeSqft: values.totalHomeSqft,
                    calculatedPercentage: values.percentage,
                    rentOrOwn: values.rentOrOwn,
                });
            }

            // Only show results page if there are reimbursable expenses
            const hasExpenses = hasReimbursableExpenses(cleanedValues);
            setShowQuestionnaire(!hasExpenses);
            stableSetCanProceed(true);

            // Don't create accountable plan here - wait until user fills out monthly expenses
            // The accountable plan will be created in handleComplete when results page is completed

            // If no reimbursements needed, show message and complete step
            if (hasNoReimbursements(cleanedValues)) {
                setShowNoReimbursementsMessage(true);
                setShowQuestionnaire(false);

                // Awaited rather than fired into a bare setTimeout so this stays part of the promise
                // handleComplete returns: the footer button's pending state then covers the write,
                // which is what keeps it disabled against a second submission. Navigation is not
                // done here — handleComplete owns the single onNext call once this settles.
                await new Promise<void>((resolve) => {
                    setTimeout(resolve, NO_REIMBURSEMENTS_MESSAGE_DURATION_MS);
                });

                try {
                    await completeOnboardingStep.mutateAsync({
                        step: 'home_office_setup',
                        data: {
                            userAnswers: values,
                            completed_at: new Date().toISOString(),
                            no_reimbursements_needed: true,
                        },
                    });
                } catch (error) {
                    // Don't block the UI flow if completion tracking fails. Swallowed rather than
                    // rethrown to preserve the existing behaviour that the member advances anyway —
                    // that silent progress loss is a known pre-existing defect tracked separately,
                    // not something this change is fixing.
                }
            }
        };

        // Helper function to render the appropriate content
        const renderContent = () => {
            if (showQuestionnaire) {
                return (
                    <ReimbursementQuestionnaire
                        ref={questionnaireRef}
                        initialValues={formValues}
                        homeAddress={homeAddress}
                        onSubmit={handleSubmit}
                        setCanProceed={stableSetCanProceed}
                    />
                );
            }

            if (showNoReimbursementsMessage) {
                return (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h1" sx={{ lineHeight: 1.2, mb: 2 }}>
                            No reimbursements needed
                        </Typography>
                        <Typography variant="h4" color="text.secondary">
                            Since you don&apos;t drive for business and rent office space, you don&apos;t have any
                            personal expenses to reimburse yourself for.
                        </Typography>
                    </Box>
                );
            }

            return (
                <ReimbursementResults
                    userAnswers={getAnswersForValidation(formValues)}
                    onExpenseDataChange={setMonthlyExpenseData}
                />
            );
        };

        // Ensure Complete button is enabled when on results page
        React.useEffect(() => {
            if (!showQuestionnaire && questionnaireComplete) {
                stableSetCanProceed(true);
            }
        }, [showQuestionnaire, questionnaireComplete, stableSetCanProceed]);

        // Handle actual completion after confirmation
        const handleConfirmComplete = async () => {
            if (isConfirmingRef.current) {
                return;
            }
            isConfirmingRef.current = true;
            setIsConfirming(true);

            if (member?.id) {
                try {
                    const accountablePlans = transformUserAnswersToAccountablePlans(
                        formValues,
                        monthlyExpenseData,
                        member.id,
                        mileageRates || []
                    );

                    // Create a plan for each month that has expenses
                    const planPromises = accountablePlans.map(async (plan) => {
                        // Convert to AccountablePlanData format (strings) for API
                        const apiData = undoCastPlanValues(plan);
                        return createAccountablePlanRequest(apiData);
                    });

                    await Promise.all(planPromises);

                    // Track reimbursement section completion
                    sendTrackingEvent('Reimbursement Section Completed', {
                        category: 'accounting_setup',
                        action: 'section_completed',
                        stepName: 'reimbursement_overview',
                        userType: member?.usertype,
                        accountablePlansCreated: accountablePlans.length,
                        monthlyExpenseData: Object.keys(monthlyExpenseData).length,
                    });
                } catch (error) {
                    // Don't block the UI flow if saving fails
                    // Error is silently handled to maintain user experience
                }
            }

            try {
                await completeOnboardingStep.mutateAsync({
                    step: 'home_office_setup',
                    data: {
                        userAnswers: formValues,
                        completed_at: new Date().toISOString(),
                    },
                });
            } catch (error) {
                // Don't block navigation if completion tracking fails
                // Error is silently handled to maintain user experience
            }

            // Released only now, so the footer's Complete button stays disabled for the whole
            // confirm path; Finish stays loading until then, so a second click can't create a
            // duplicate plan for every month.
            setShowConfirmModal(false);
            isConfirmingRef.current = false;
            setIsConfirming(false);
            settleConfirmCompletion();

            // Navigate to next step
            onNext?.();
        };

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            showQuestionnaire,
            handleComplete: async () => {
                if (showQuestionnaire) {
                    // Get current form values from questionnaire and submit
                    const currentValues = questionnaireRef.current?.getCurrentValues() || formValues;
                    await handleSubmit(currentValues);

                    // The no-reimbursement branch of handleSubmit has finished showing its message
                    // and recording the step by the time this resolves, so this is the only onNext
                    // for that path — the write no longer fires its own and navigate twice.
                    const cleanedCurrentValues = getAnswersForValidation(currentValues);
                    if (hasNoReimbursements(cleanedCurrentValues)) {
                        onNext?.();
                    }
                    // If there are reimbursable expenses, the component will show the monthly expense page
                    // and the user will need to click "Complete" again to proceed
                } else {
                    // Show confirmation modal before completing, and stay pending until the member
                    // is done with it — handleConfirmComplete's writes run after the modal closes,
                    // so resolving when it opens would hand the footer button back mid-write.
                    setShowConfirmModal(true);
                    await new Promise<void>((resolve) => {
                        confirmCompletionResolveRef.current = resolve;
                    });
                }
            },
            // Back never leaves this step: the import before it can't be redone once completed.
            handleBack: () => {
                setShowQuestionnaire(true);
                setQuestionnaireComplete(false);
            },
        }));

        return (
            <>
                <Box
                    sx={{
                        maxWidth: {
                            xs: '100%',
                            sm: '560px',
                        },
                        width: '100%',
                    }}>
                    {renderContent()}
                </Box>

                {/* Confirmation Modal */}
                <ConfirmationDialog
                    open={showConfirmModal}
                    handleClose={isConfirming ? undefined : cancelConfirmModal}
                    title="Done adding your mixed-use expenses?"
                    message={
                        <>
                            <DialogContentText variant="body1" color="text.primary">
                                Make sure you&apos;ve added all mixed-use expenses from the past two months.
                            </DialogContentText>
                            <DialogContentText variant="body1" color="text.primary">
                                Going forward, you can add new mixed-use expenses through{' '}
                                <Typography component="span" variant="bodySemiBold">
                                    Reimbursements
                                </Typography>
                                .
                            </DialogContentText>
                        </>
                    }
                    reject={{ label: 'Go back', variant: 'text', onClick: cancelConfirmModal, disabled: isConfirming }}
                    accept={{ label: 'Finish', onClick: handleConfirmComplete, loading: isConfirming }}
                />
            </>
        );
    }
);

ReimbursementOverview.defaultProps = defaultProps;
ReimbursementOverview.displayName = 'ReimbursementOverview';

export default ReimbursementOverview;
